var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { client } from './client';
function levelQuery(filter) {
    const params = new URLSearchParams();
    if (filter === null || filter === void 0 ? void 0 : filter.creator)
        params.set('creator', filter.creator);
    if (filter === null || filter === void 0 ? void 0 : filter.moderationStatus)
        params.set('moderationStatus', filter.moderationStatus);
    if (filter === null || filter === void 0 ? void 0 : filter.challengeId)
        params.set('challengeId', filter.challengeId);
    const qs = params.toString();
    return qs ? `?${qs}` : '';
}
// Get a list of levels (active and archived), optionally filtered by creator
// and/or moderation status. The list endpoint returns bare Level rows — node
// payloads are only assembled by getLevelById.
export function getLevels(filter) {
    return __awaiter(this, void 0, void 0, function* () {
        return client.get(`/levels${levelQuery(filter)}`);
    });
}
// Distinct creator usernames (for the admin moderation filter chips).
export function getCreators(filter) {
    return __awaiter(this, void 0, void 0, function* () {
        return client.get(`/levels/creators${levelQuery(filter)}`);
    });
}
// --- Moderation pipeline transitions ---
// Creator submits a level for moderation (Draft/Rejected -> Pending).
export function submitLevel(id) {
    return __awaiter(this, void 0, void 0, function* () {
        return client.post(`/levels/${id}/submit`, {});
    });
}
// ── Stage 2 (images) + the publication handshake ────────────────────────────────
export function submitImages(id) {
    return __awaiter(this, void 0, void 0, function* () {
        return client.post(`/levels/${id}/submit-images`, {});
    });
}
export function approveImages(id) {
    return __awaiter(this, void 0, void 0, function* () {
        return client.post(`/levels/${id}/images/approve`, {});
    });
}
export function requestImageChanges(id, reason) {
    return __awaiter(this, void 0, void 0, function* () {
        return client.post(`/levels/${id}/images/request-changes`, { reason });
    });
}
/** Moderator: undo a mistaken images acceptance — the set returns UNDER REVIEW.
 *  Refused while the level is cleared for publication (cancel that first). */
export function revokeImagesApproval(id) {
    return __awaiter(this, void 0, void 0, function* () {
        return client.post(`/levels/${id}/images/revoke-approval`, {});
    });
}
/** Moderator: clear the level for publication (the game backend takes it from there). */
export function publishLevel(id) {
    return __awaiter(this, void 0, void 0, function* () {
        return client.post(`/levels/${id}/publish`, {});
    });
}
/** Moderator: cancel an accidental publish — only while the game hasn't picked it up. */
export function unpublishLevel(id) {
    return __awaiter(this, void 0, void 0, function* () {
        return client.post(`/levels/${id}/unpublish`, {});
    });
}
// Restore a default-derived level (one with a defaultKey) to its template's pristine content.
export function resetLevelToDefault(id) {
    return __awaiter(this, void 0, void 0, function* () {
        return client.post(`/levels/${id}/reset-to-default`, {});
    });
}
// Creator withdraws a pending submission (Pending -> Draft).
export function withdrawLevel(id) {
    return __awaiter(this, void 0, void 0, function* () {
        return client.post(`/levels/${id}/withdraw`, {});
    });
}
// Admin approves a pending level (Pending -> Approved).
export function approveLevel(id) {
    return __awaiter(this, void 0, void 0, function* () {
        return client.post(`/levels/${id}/approve`, {});
    });
}
// Admin rejects a pending level with a reason (Pending -> Rejected).
export function rejectLevel(id, reason) {
    return __awaiter(this, void 0, void 0, function* () {
        return client.post(`/levels/${id}/reject`, { reason });
    });
}
// Get a specific level by ID with all its nodes
export function getLevelById(id) {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            // Fetch the level data with its nodes from the direct response
            const level = yield client.get(`/levels/${id}`);
            // Check and initialize nodes if they are missing
            if (!level.nodes) {
                level.nodes = {};
            }
            else if (typeof level.nodes === 'string') {
                // If nodes is provided as a JSON string, parse it
                try {
                    level.nodes = JSON.parse(level.nodes);
                }
                catch (parseError) {
                    console.error('Error parsing nodes JSON:', parseError);
                    level.nodes = {};
                }
            }
            return level;
        }
        catch (error) {
            console.error(`Error loading level ${id}:`, error);
            throw error;
        }
    });
}
// Create a new level
// The data type for creation no longer includes id, isArchived, isActive
export function createLevel(levelData) {
    return __awaiter(this, void 0, void 0, function* () {
        return client.post('/levels', levelData);
    });
}
/**
 * Update a level's details (without modifying nodes)
 */
export function updateLevel(levelId, levelData) {
    return __awaiter(this, void 0, void 0, function* () {
        // Make sure we have a valid level object before sending
        if (!levelData || typeof levelData !== 'object') {
            throw new Error('Invalid level data: level object is missing');
        }
        try {
            // Create a copy of the data without nodes for the main level update
            const { nodes, id } = levelData, levelBasicData = __rest(levelData, ["nodes", "id"]);
            // Save the level's basic information
            const savedLevel = yield client.patch(`/levels/${levelId}`, levelBasicData);
            // Save the nodes separately if they exist and are not empty
            if (nodes && Object.keys(nodes).length > 0) {
                // Save each node via nodesDb
                yield saveNodesIndividually(levelId, nodes);
            }
            // Return the full level with its nodes
            const updatedLevel = Object.assign(Object.assign({}, savedLevel), { nodes: nodes || {} });
            return updatedLevel;
        }
        catch (error) {
            console.error(`Error updating level ${levelId}:`, error);
            if (error.response) {
                console.error(`Server responded with status ${error.response.status}:`, error.response.data);
                if (error.response.data && error.response.data.message) {
                    throw new Error(`Server error: ${error.response.data.message}`);
                }
            }
            throw error;
        }
    });
}
// DELETE a level (archive)
export const deleteLevel = (levelId) => __awaiter(void 0, void 0, void 0, function* () {
    yield client.delete(`/levels/${levelId}`);
    // No content expected on successful delete
});
// POST restore an archived level
export const restoreLevel = (levelId) => __awaiter(void 0, void 0, void 0, function* () {
    return yield client.patch(`/levels/${levelId}/restore`, {}); // Use PATCH as per backend
});
// PATCH update isActive status
export const updateLevelStatus = (levelId, isActive) => __awaiter(void 0, void 0, void 0, function* () {
    return yield client.patch(`/levels/${levelId}/status`, { isActive });
});
// -------- Node Specific API Calls --------
// Helper function to save all of a level's nodes via the API.
// Tries every node, then reports all failures at once — a failed save must be
// visible to the caller, not silently swallowed.
function saveNodesIndividually(levelId, nodes) {
    return __awaiter(this, void 0, void 0, function* () {
        const failedCoords = [];
        for (const [coordString, nodeData] of Object.entries(nodes)) {
            try {
                yield updateNodeData(levelId, coordString, nodeData);
            }
            catch (error) {
                console.error(`Error saving node ${coordString}:`, error);
                failedCoords.push(coordString);
            }
        }
        if (failedCoords.length > 0) {
            throw new Error(`Failed to save nodes: ${failedCoords.join(', ')}`);
        }
    });
}
/**
 * Update data for a specific node within a level.
 * Throws on failure — the server is the only source of truth for node data.
 */
export function updateNodeData(levelId, coordString, nodeData) {
    return __awaiter(this, void 0, void 0, function* () {
        return client.patch(`/levels/${levelId}/nodes/${coordString}`, nodeData);
    });
}
// Permanently delete a level (use with caution!)
export function deleteLevelPermanently(levelId) {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            // Use a distinct endpoint for permanent deletion
            yield client.delete(`/levels/${levelId}/permanent`);
        }
        catch (error) {
            console.error('Error in deleteLevelPermanently API call:', error);
            throw error; // Re-throw for the component to handle
        }
    });
}
